import type {
  DraftState,
  PlannedOrderSettings,
  QuestionAnswer,
  WizardQuestion,
  WizardStep
} from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { hasKeyColumn } from './sheet/sheetMapping.ts'
import { applyControlLabels, controlDecision } from './sampleLabels.ts'
import { compactPools, placedGroups, poolSuggestion, runsPending, suggestionPossible } from './pools.ts'
import { applySheetPools, sheetPoolsOf, sheetSuggestion } from './sheet/sheetRoles.ts'
import { fillPair, groupsWithoutPrimers, primerGapSettled } from './primerCheck.ts'
import { archiveSamples, isArchiveOrder } from './archive.ts'
import { labelNegativeControls } from './negativeControls.ts'
import {
  readsTargetConflict,
  readsTargetKept,
  targetConflict,
  targetSuggestions,
  withResolvedFormat,
  withResolvedTarget
} from './targetPlan.ts'
import { regionOf, settledTrimming, withTargetTaxonomy } from './orderSettingsPlan.ts'
import { REJECTION_TEXT } from './itemPlan.ts'
import { columnTargets, retarget, withColumnTargets } from './sheet/sheetMapping.ts'
import { getMergedSampleConfigurationForPrimersRemoved } from '../../Util/sequencingPlatform.ts'
import { SampleConfigurationTypes } from '../../Generated/SampleConfigurationType.ts'

const PROCESSING_QUESTION_ID = 'processing-type-conflict'
const SHEET_KEY_QUESTION_ID = 'sheet-key-column'

/**
 * One sample per file pair, or several? The multiplex detector could not tell for untrimmed pairs, and
 * the format decides how the pipeline treats every file, so the order waits for the answer.
 */
const formatQuestions = (draft: DraftState): WizardQuestion[] => {
  const { discovery, settings } = draft
  if (!discovery || !settings || draft.orderId || settings.sampleConfiguration.source === 'answered') {
    return []
  }
  const trimming = draft.trimmingAnswer === 'on' ? 'UNTRIMMED' : discovery.trimming
  const unclear = discovery.layout.value === 'PAIRED' && trimming === 'UNTRIMMED' && discovery.multiplexing === 'UNKNOWN'
  const lowArchive = settings.sampleConfiguration.value === 'COMBINED_SAMPLE_PAIRS' && discovery.classifier.confidence === 'low'
  if (!unclear && !lowArchive) {
    return []
  }
  return [{ id: 'format-multiplex', kind: 'format-multiplex', blocking: true, reason: discovery.classifier.reason }]
}

const awaitingLayout = (draft: DraftState) =>
  Object.values(draft.files).filter((file) => file.kind === 'fastq' && file.rejection === REJECTION_TEXT['awaiting-layout'])

/**
 * The read layout and whether the primers are on decide the format, and only the files can tell: when
 * they do not, the order waits for the lab. Files without mates wait for theirs, or are merged reads.
 */
const layoutQuestions = (draft: DraftState): WizardQuestion[] => {
  const { discovery, settings } = draft
  if (!discovery || !settings || draft.orderId || settings.sampleConfiguration.source !== 'undetermined') {
    return []
  }
  const unmated = discovery.layout.unmated
  if (unmated) {
    const files = awaitingLayout(draft).length
    return draft.layoutAnswer || files === 0
      ? []
      : [{ id: 'read-layout', kind: 'read-layout', blocking: true, files, ...unmated, trimming: settledTrimming(discovery) }]
  }
  if (settledTrimming(discovery) !== 'UNKNOWN' || draft.trimmingAnswer) {
    return []
  }
  const merged = SampleConfigurationTypes[settings.sampleConfiguration.value].impliesMerged
  const { nearPairFiles = 0, judgedFiles = 0 } = discovery.classifier
  /* no pair at all is no lean: trimmed reads and an unregistered primer look the same */
  const lean = nearPairFiles > 0 && nearPairFiles * 2 > judgedFiles ? ('on' as const) : undefined
  return [{ id: 'primer-trimming', kind: 'primer-trimming', blocking: true, merged, ...(lean ? { lean } : {}) }]
}

/** The target is never assumed: what no primer decides, the lab picks; a sheet contradicting it, the lab settles. */
const targetQuestions = (draft: DraftState): WizardQuestion[] => {
  const settings = draft.settings
  if (!settings) {
    return []
  }
  if (settings.targetTaxonomy.source === 'undetermined') {
    return [
      {
        id: 'target-taxon',
        kind: 'target-taxon',
        blocking: true,
        reason: settings.targetTaxonomy.reason,
        suggestions: targetSuggestions(draft)
      }
    ]
  }
  return [targetConflict(draft), readsTargetConflict(draft)].flatMap((question) => (question ? [question] : []))
}

const settingsQuestions = (draft: DraftState): WizardQuestion[] => {
  const conflict = draft.settings?.processingTypeConflict
  const asked = [...formatQuestions(draft), ...layoutQuestions(draft), ...targetQuestions(draft)]
  if (!conflict || draft.orderId) {
    return asked
  }
  return [
    ...asked,
    {
      id: PROCESSING_QUESTION_ID,
      kind: 'processing-type-conflict',
      blocking: true,
      preferred: conflict.preferred,
      detectedTaxon: draft.settings?.targetTaxonomy.value ?? 'OTHER',
      allowedTaxa: conflict.allowedTaxa
    }
  ]
}

const archiveSheetQuestions = (draft: DraftState): WizardQuestion[] => {
  const archives = Object.keys(draft.groups).length
  if (archives === 0) {
    return []
  }
  if (!draft.sheet) {
    return [{ id: 'archive-needs-sheet', kind: 'archive-needs-sheet', blocking: false, archives }]
  }
  return []
}

const sheetQuestions = (draft: DraftState): WizardQuestion[] => {
  const sheet = draft.sheet
  if (isArchiveOrder(draft)) {
    return sheet && !hasKeyColumn(sheet.mapping) && Object.keys(draft.groups).length > 1
      ? [{ id: SHEET_KEY_QUESTION_ID, kind: 'sheet-key-column', blocking: false, reason: 'No column says which archive a sample is in' }]
      : archiveSheetQuestions(draft)
  }
  if (!sheet) {
    return []
  }
  if (!hasKeyColumn(sheet.mapping)) {
    return [{ id: SHEET_KEY_QUESTION_ID, kind: 'sheet-key-column', blocking: false, reason: 'No column names the files or the samples' }]
  }
  return []
}

/** In an archive order the controls are sheet samples inside the archives, judged by their names. */
const archiveControlDecision = (draft: DraftState) => {
  const samples = Object.values(archiveSamples(draft)).flat()
  const result = labelNegativeControls(samples.map((sample) => ({ key: sample.key, fileName: '', sampleName: sample.name })))
  return {
    flagged: new Set(result.flagged),
    decision: result.decision,
    total: samples.length,
    userSet: samples.some((sample) => sample.typeSource === 'user')
  }
}

const controlQuestions = (draft: DraftState): WizardQuestion[] => {
  const groups = Object.values(draft.groups)
  if (isArchiveOrder(draft)) {
    const archive = draft.sheet && !draft.controlsAnswer ? archiveControlDecision(draft) : undefined
    if (!archive || archive.total === 0 || archive.userSet) {
      return []
    }
    if (archive.decision === 'none-found') {
      return [{ id: 'controls-none-found', kind: 'controls-none-found', blocking: false }]
    }
    return archive.decision === 'too-many'
      ? [{ id: 'controls-too-many', kind: 'controls-too-many', blocking: false, flagged: archive.flagged.size, total: archive.total }]
      : []
  }
  /* the user's marks or the sheet's type column settle it; the names are only asked about when neither does */
  if (groups.length === 0 || draft.controlsAnswer || groups.some((group) => group.typeSource === 'user' || group.typeSource === 'sheet')) {
    return []
  }
  const { flagged, decision } = controlDecision(draft)
  if (decision === 'none-found') {
    return [{ id: 'controls-none-found', kind: 'controls-none-found', blocking: false }]
  }
  if (decision === 'too-many') {
    return [{ id: 'controls-too-many', kind: 'controls-too-many', blocking: false, flagged: flagged.size, total: groups.length }]
  }
  return []
}

/** The file names may still suggest pools: some files' sequencing runs are being read. */
export const poolSuggestionPending = (draft: DraftState): boolean =>
  !draft.poolsAnswered && !isArchiveOrder(draft) && placedGroups(draft).length > 0 && suggestionPossible(draft) && runsPending(draft)

/**
 * Whether the samples share their negative controls is asked only when something says they may not: the
 * file names suggest groups, or the items already sit in more than one pool. Otherwise the order is one
 * pool, and nothing is asked.
 */
const poolQuestions = (draft: DraftState): WizardQuestion[] => {
  const groups = placedGroups(draft)
  if (draft.poolsAnswered || isArchiveOrder(draft) || groups.length === 0) {
    return []
  }
  const several = new Set(groups.map((group) => group.pool ?? 0)).size > 1
  /* a sheet's pool column replaces the file names' suggestion; one it could apply on its own already did */
  const sheetPools = sheetPoolsOf(draft)
  const fromSheet = sheetPools && sheetPools.count > 1 ? sheetSuggestion(draft, sheetPools) : undefined
  const suggestion = fromSheet ?? (poolSuggestionPending(draft) ? undefined : poolSuggestion(draft))
  if (!suggestion && !several) {
    return []
  }
  return [{ id: 'pools', kind: 'pools', blocking: false, ...(suggestion ? { suggestion } : {}) }]
}

const primerQuestions = (draft: DraftState): WizardQuestion[] => {
  /* whether primers are required follows from the format, which a question settles first */
  if (draft.settings?.sampleConfiguration.source === 'undetermined') {
    return []
  }
  const missing = groupsWithoutPrimers(draft)
  if (missing.length === 0 || !primerGapSettled(draft, missing)) {
    return []
  }
  const suggestion = fillPair(draft)
  return [
    {
      id: 'primers-missing',
      kind: 'primers-missing',
      blocking: false,
      groupKeys: missing.map((group) => group.key),
      controls: missing.filter((group) => group.itemType === 'NEGATIVE_CONTROL').length,
      ...(suggestion ? { suggestion } : {}),
      sheetUsed: !!draft.sheet || !!draft.sheetRef
    }
  ]
}

const questionsOf = (draft: DraftState): WizardQuestion[] => [
  ...settingsQuestions(draft),
  ...sheetQuestions(draft),
  ...controlQuestions(draft),
  ...poolQuestions(draft),
  ...primerQuestions(draft)
]

export const openQuestions = (draft: DraftState): WizardQuestion[] => questionsOf(withResolvedFormat(withResolvedTarget(draft)))

/** Labels, a target and format the files or the sheet settle, and the question queue, recomputed after anything that can change them. */
export const refreshDerived = (draft: DraftState): DraftState => {
  const resolved = withResolvedFormat(withResolvedTarget(applySheetPools(applyControlLabels(draft))))
  return { ...resolved, questions: questionsOf(resolved) }
}

export const hasBlockingQuestion = (draft: DraftState): boolean => openQuestions(draft).some((question) => question.blocking)

export const QUESTION_STEP: Record<WizardQuestion['kind'], WizardStep> = {
  'processing-type-conflict': 'files',
  'format-multiplex': 'files',
  'sheet-key-column': 'metadata',
  'controls-none-found': 'pools',
  'controls-too-many': 'pools',
  pools: 'pools',
  'primers-missing': 'primers',
  'archive-needs-sheet': 'pools',
  'read-layout': 'files',
  'primer-trimming': 'files',
  'target-taxon': 'files',
  'target-conflict': 'files',
  'target-reads-conflict': 'files'
}

/**
 * Whether the step's own content holds its primary action, so the footer's Next stays secondary: an
 * open question to answer on the step (the pools question included, which its step shows itself).
 */
export const stepLeadsWithQuestion = (draft: DraftState, step: WizardStep): boolean =>
  draft.questions.some((question) => QUESTION_STEP[question.kind] === step)

/** The open questions shown as cards, all or one step's: the pools question's card is its step's own. */
export const cardQuestions = (draft: DraftState, step?: WizardStep): WizardQuestion[] =>
  draft.questions.filter((question) => question.kind !== 'pools' && (!step || QUESTION_STEP[question.kind] === step))

export type PoolsQuestion = Extract<WizardQuestion, { kind: 'pools' }>

export const poolsQuestion = (draft: DraftState): PoolsQuestion | undefined =>
  draft.questions.find((question): question is PoolsQuestion => question.kind === 'pools')

export const controlsAsked = (draft: DraftState): boolean =>
  draft.questions.some((question) => question.kind === 'controls-none-found' || question.kind === 'controls-too-many')

const answeredSettings = (
  settings: PlannedOrderSettings,
  answer: Extract<QuestionAnswer, { kind: 'processing-type-conflict' }>
): PlannedOrderSettings => {
  const conflict = settings.processingTypeConflict
  if (!conflict) {
    return settings
  }
  if (answer.choice === 'metabarcoding') {
    return {
      ...settings,
      processingTypeConflict: undefined,
      answeredConflict: conflict,
      processingType: {
        value: 'METABARCODING',
        source: 'answered',
        reason: `processed as metabarcoding to keep ${settings.targetTaxonomy.value}`
      }
    }
  }
  return {
    ...settings,
    processingTypeConflict: undefined,
    answeredConflict: conflict,
    targetTaxonomy: { value: answer.taxon, source: 'answered', reason: `chosen to keep ${conflict.preferred}` },
    targetRegion: regionOf({ value: answer.taxon, source: 'answered' }, settings.targetRegionEntered),
    processingType: { value: conflict.preferred, source: 'answered', reason: "your organisation's default, kept" }
  }
}

/** The draft with new settings; once the order exists they are to be written into it. */
const withSettings = (draft: DraftState, settings: PlannedOrderSettings): DraftState =>
  draft.orderId
    ? { ...draft, settings, settingsWrite: { status: 'pending', revision: (draft.settingsWrite?.revision ?? 0) + 1 } }
    : { ...draft, settings }

const ANSWERED = 'chosen by you'

/** Files waiting on the layout answer become one item each, as merged reads. */
const asMerged = (draft: DraftState, settings: PlannedOrderSettings, trimmed: boolean): DraftState => {
  const waiting = awaitingLayout(draft)
  const files = { ...draft.files }
  const groups = { ...draft.groups }
  for (const file of waiting) {
    files[file.key] = { ...file, rejection: undefined, upload: file.upload ?? { status: 'pending', progress: 0 } }
    groups[file.key] = { key: file.key, forward: file.key, item: { status: 'waiting' } }
  }
  const value = getMergedSampleConfigurationForPrimersRemoved(trimmed)
  return {
    ...draft,
    files,
    groups,
    mergedAnswer: true,
    settings: { ...settings, sampleConfiguration: { value, source: 'answered', reason: 'merged reads, as you said' } }
  }
}

const awaitingMates = (draft: DraftState): DraftState => {
  const files = { ...draft.files }
  for (const file of awaitingLayout(draft)) {
    files[file.key] = { ...file, rejection: REJECTION_TEXT['awaiting-mate'] }
  }
  return { ...draft, files, layoutAnswer: 'add-mates' }
}

/** The target the lab keeps against the sheet: the sheet's primer columns are no longer imported. */
const keepTarget = (draft: DraftState, settings: PlannedOrderSettings): DraftState => {
  const sheet = draft.sheet
  const target = settings.targetTaxonomy
  const kept = {
    ...settings,
    targetTaxonomy: { value: target.value, source: 'answered' as const, reason: `${target.value} kept over the sheet's primers` }
  }
  if (!sheet) {
    return { ...draft, settings: kept }
  }
  let targets = columnTargets(sheet)
  for (const column of [sheet.mapping.forwardPrimer, sheet.mapping.reversePrimer]) {
    if (column) {
      targets = retarget(targets, column, 'drop')
    }
  }
  return { ...draft, settings: kept, sheet: withColumnTargets(sheet, targets) }
}

/** The draft with an answer applied: settings, the sheet mapping, labels or pools. */
export const applyAnswer = (draft: DraftState, answer: QuestionAnswer): DraftState => {
  switch (answer.kind) {
    case 'read-layout':
      if (!draft.settings) {
        return draft
      }
      return answer.choice === 'merged' ? asMerged(draft, draft.settings, answer.trimmed) : awaitingMates(draft)
    case 'primer-trimming': {
      if (!draft.settings) {
        return draft
      }
      const merged = SampleConfigurationTypes[draft.settings.sampleConfiguration.value].impliesMerged
      const value = merged
        ? getMergedSampleConfigurationForPrimersRemoved(answer.trimmed)
        : answer.trimmed
          ? 'TRIMMED_SAMPLES'
          : 'SINGLE_SAMPLE_PAIRS'
      const reason = answer.trimmed ? 'primers removed, as you said' : 'primers on the reads, as you said'
      /* untrimmed pairs may hold several samples each: asked next, as detection would */
      const formatOpen = value === 'SINGLE_SAMPLE_PAIRS' && draft.discovery?.layout.value === 'PAIRED'
      return {
        ...draft,
        trimmingAnswer: answer.trimmed ? 'removed' : 'on',
        settings: { ...draft.settings, sampleConfiguration: { value, source: formatOpen ? 'undetermined' : 'answered', reason } }
      }
    }
    case 'target-taxon':
      return draft.settings
        ? withSettings(
            draft,
            withTargetTaxonomy(draft.settings, { value: answer.taxon, source: 'answered', reason: ANSWERED }, draft.discovery)
          )
        : draft
    case 'target-conflict':
      if (!draft.settings) {
        return draft
      }
      return answer.choice === 'sheet'
        ? withSettings(
            draft,
            withTargetTaxonomy(draft.settings, { value: answer.taxon, source: 'answered', reason: ANSWERED }, draft.discovery)
          )
        : keepTarget(draft, draft.settings)
    case 'target-reads-conflict':
      if (!draft.settings) {
        return draft
      }
      return answer.choice === 'reads'
        ? withSettings(
            draft,
            withTargetTaxonomy(draft.settings, { value: answer.taxon, source: 'answered', reason: ANSWERED }, draft.discovery)
          )
        : {
            ...draft,
            acknowledged: [...(draft.acknowledged ?? []), readsTargetKept(draft.settings.targetTaxonomy.value)]
          }
    case 'processing-type-conflict':
      return draft.settings ? { ...draft, settings: answeredSettings(draft.settings, answer) } : draft
    case 'sheet-key-column': {
      if (!draft.sheet) {
        return draft
      }
      const keyColumns = new Set(Object.values(answer.mapping))
      const extraColumns = Object.fromEntries(Object.entries(draft.sheet.extraColumns).filter(([column]) => !keyColumns.has(column)))
      return {
        ...draft,
        sheet: { ...draft.sheet, mapping: { ...draft.sheet.mapping, ...answer.mapping }, extraColumns, revision: draft.sheet.revision + 1 }
      }
    }
    case 'controls-none-found':
    case 'controls-too-many':
      return { ...draft, controlsAnswer: answer.choice }
    case 'format-multiplex': {
      if (!draft.settings) {
        return draft
      }
      const value = answer.choice === 'combined' ? 'COMBINED_SAMPLE_PAIRS' : 'SINGLE_SAMPLE_PAIRS'
      const reason = answer.choice === 'combined' ? 'several samples per file pair, as you said' : 'one sample per file pair, as you said'
      return { ...draft, multiplexAnswer: answer.choice, settings: { ...draft.settings, sampleConfiguration: { value, source: 'answered', reason } } }
    }
    case 'primers-missing': {
      const primers = { forward: answer.forward.trim().toUpperCase(), reverse: answer.reverse.trim().toUpperCase() }
      const groups = Object.fromEntries(
        Object.entries(draft.groups).map(([key, group]) => [
          key,
          answer.groupKeys.includes(key) ? { ...group, manualPrimers: primers } : group
        ])
      )
      return { ...draft, groups }
    }
    case 'pools': {
      if (answer.choice === 'one') {
        const groups = Object.fromEntries(Object.entries(draft.groups).map(([key, group]) => [key, { ...group, pool: undefined }]))
        return { ...draft, groups, poolsAnswered: true, poolsSource: 'one' }
      }
      const pools = compactPools(answer.pools)
      const groups = Object.fromEntries(
        Object.entries(draft.groups).map(([key, group]) => [key, { ...group, pool: pools[key] ? pools[key] : undefined }])
      )
      return {
        ...draft,
        groups,
        poolsAnswered: true,
        poolsSource: answer.source === 'suggested' ? answer.method : 'entered'
      }
    }
  }
}
